import React from "react";
import { createUseStyles } from "react-jss";
import classNames from "classnames";
import PropTypes from "prop-types";
import { SVGAvatarUser, SVGLike, SVGDislike } from "Constants/SVG";
import { ReviewContainerV3 } from "Containers/Review";
import { Tooltip, TooltipBody } from "ComponentsUI/Tooltip";
import LazyImage from "ComponentsUI/Image";

const useStyles = createUseStyles((theme) => ({
	root: {
		width: "760px",
		margin: "20px 0",
		padding: "15px 20px",
		border: `1px solid ${theme.palette.colors.cloudyBlue}`,
		borderRadius: "10px",
		backgroundColor: theme.palette.colors.white,
		fontFamily: theme.fonts.medium,
	},
	rootAnswer: {
		width: "100%",
		margin: "30px 0 0 0",
		paddingLeft: "20px",
		borderLeft: `1px solid ${theme.palette.colors.cloudyBlue}`,
		backgroundColor: theme.palette.colors.white,
		fontFamily: theme.fonts.medium,
	},
	onlyReviewsView: {
		width: "780px",
	},
	hide: {
		display: "none",
	},
	reviewInfo: {
		display: "flex",
		alignItems: "center",
		marginBottom: "12px",
	},
	avatar: {
		width: "50px",
		height: "50px",
		borderRadius: "50%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		marginRight: "8px",
	},
	userInfo: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "flex-start",
		flexGrow: 1,
	},
	userName: {
		marginBottom: "10px",
		fontSize: "18px",
		lineHeight: "14px",
		color: theme.palette.colors.darkSlateBlue2,
	},
	reviewDate: {
		fontSize: "14px",
		color: theme.palette.colors.cloudyBlue,
	},
	score: {
		// display: "flex",
		display: "none",
	},
	like: {
		display: "flex",
	},
	likeCount: {
		margin: "0 15px 0 5px",
		color: theme.palette.colors.liteGreen,
	},
	dislike: {
		display: "flex",
	},
	dislikeCount: {
		marginLeft: "5px",
		color: theme.palette.colors.giftPink,
	},
	providerName: {
		margin: "0 auto 10px",
		color: theme.palette.colors.darkSlateBlue2,
		fontSize: "14px",
	},
	reviewText: {
		margin: "0 auto 10px",
		color: theme.palette.colors.oneMoreGrey,
	},
	moreElementWrapper: {
		display: "flex",
		marginBottom: "10px",
		flexDirection: "row-reverse",
		justifyContent: "space-between",
		alignItems: "center",
	},
	reviewId: {
		color: theme.palette.colors.oneMoreGrey,
		fontSize: "12px",
		lineHeight: "14px",
	},
	readMoreButton: {
		// marginBottom: "10px",
		color: theme.palette.colors.violet,
		cursor: "pointer",
		lineHeight: "20px",
	},
	proofReviewWrapper: {
		display: "flex",
		alignItems: "center",
		marginRight: "48px",
	},
	iconCheck: {
		color: theme.palette.colors.liteGreen,
		fontSize: "14px",
		fontWeight: "bold",
	},
	proofedText: {
		margin: "auto 8px auto 5px",
		fontSize: "16px",
		color: theme.palette.colors.oneMoreGrey,
	},
	iconQuestion: {
		color: theme.palette.colors.disabled,
	},
	[`@media (min-width: ${theme.media.md}) and (max-width: ${theme.media.lg})`]: {
		root: {
			width: "540px",
		},
		onlyReviewsView: {
			width: "780px",
		},
		reviewId: {
			display: "none",
		},
	},
	[`@media (min-width: ${theme.media.sm}) and (max-width: ${theme.media.md})`]: {
		root: {
			width: "390px",
		},
		reviewId: {
			display: "none",
		},
	},
	[`@media (max-width: ${theme.media.sm})`]: {
		root: {
			width: "100%",
		},
		reviewId: {
			display: "none",
		},
	},
}));

const ReviewItemV2 = (props) => {
	const {
		id,
		name,
		avatar,
		date,
		providerName,
		reviewText,
		proofed,
		onlyReviewsStyle,
		isSSR,
		showFullReview,
		answers,
		text,
		type,
		reviewsShowLength,
		position,
		showButton,
		showFullReviewHandler,
	} = props;
	const classes = useStyles();

	const generateLogo = () => {
		return <LazyImage src={avatar} className={classes.avatar} alt={name} />;
	};

	return (
		<div
			className={classNames("", {
				[classes.root]: type !== "answer",
				[classes.rootAnswer]: type === "answer",
				[classes.hide]: position > reviewsShowLength,
				[classes.onlyReviewsView]: onlyReviewsStyle,
			})}
		>
			<div className={classes.reviewInfo}>
				<div className={classes.avatar}>{avatar ? generateLogo() : SVGAvatarUser}</div>
				<div className={classes.userInfo}>
					<div className={classes.userName}>{name}</div>
					{Boolean(date) && <div className={classes.reviewDate}>{date}</div>}
				</div>
				<div className={classes.score}>
					<div className={classes.like}>
						<div>{SVGLike}</div>
						<span className={classes.likeCount}>0</span>
					</div>
					<div className={classes.dislike}>
						<div>{SVGDislike}</div>
						<span className={classes.dislikeCount}>0</span>
					</div>
				</div>
			</div>
			<p className={classes.providerName}>{providerName}</p>
			<p className={classes.reviewText}>
				{isSSR
					? reviewText
					: showFullReview
						? reviewText.substring(0, 150).concat("...")
						: reviewText}
				{}
			</p>
			<div className={classes.moreElementWrapper}>
				<div className={classes.reviewId}>{id}</div>
				<div className={classes.readMoreButton} onClick={(e) => showFullReviewHandler(e)}>
					{!isSSR && showButton && (showFullReview ? text.read_full : text.wrap)}
				</div>
			</div>
			{proofed && (
				<div className={classes.proofReviewWrapper} onClick={(e) => e.stopPropagation()}>
					<span className={classNames("icon24 icon-check", classes.iconCheck)} />
					<div className={classes.proofedText}>{text.PROOFED_REVIEW}</div>
					<Tooltip body={<TooltipBody>{text.PROOFED_REVIEW_TEXT}</TooltipBody>}>
						<span className={classNames("icon24 icon-question", classes.iconQuestion)} />
					</Tooltip>
				</div>
			)}
			{answers
				? answers.map((answer, index) => {
						return <ReviewContainerV3 key={index} type="answer" review={answer} />;
					})
				: null}
		</div>
	);
};

ReviewItemV2.propTypes = {
	name: PropTypes.string,
	avatar: PropTypes.string,
	providerName: PropTypes.string,
	date: PropTypes.string,
	reviewText: PropTypes.string,
	onlyReviewsStyle: PropTypes.bool,
	proofed: PropTypes.bool,
	isSSR: PropTypes.bool,
	showFullReview: PropTypes.bool,
	answers: PropTypes.array,
	text: PropTypes.object,
	type: PropTypes.string,
	reviewsShowLength: PropTypes.number,
	position: PropTypes.number,
	showButton: PropTypes.bool,
	showFullReviewHandler: PropTypes.func,
};

ReviewItemV2.defaultProps = {
	name: "",
	avatar: "",
	providerName: "",
	date: "",
	reviewText: "",
	onlyReviewsStyle: false,
	proofed: false,
	showFullReview: false,
	answers: [],
	text: {},
	type: "",
	reviewsShowLength: 0,
	position: 0,
	showButton: false,
	showFullReviewHandler: () => {},
};

export default ReviewItemV2;
